import {curCoreClient, DeviceCommandReq, DeviceDownlinksQueueReply, DownlinkItem, GetDeviceReq} from './CoreClient';
import _ from 'lodash';
import { HttpResponse } from '@/types/http';
import {
  Device,
  Reading,
  CombinedReading,
  Message,
  Channel,
  DeviceConfigurationSet,
} from '@/types/core';

export class DeviceService {

  public async getDevices(query: GetDevicesQuery & { initialOnly?: boolean }): Promise<HttpResponse & GetDevicesReply> {
    const isInitial = query.initialOnly ?? false;
    const deviceListReply = query.filter ? await curCoreClient.searchDevices({query:query.filter, limit:1000}) 
    : isInitial
    ? await curCoreClient.getDevices({ skip: 0, limit: 1000 })
    : await this.getAllDevices();
    
    if(deviceListReply.errorMsg)
      return Promise.resolve({errorMsg:deviceListReply.errorMsg, httpCode:deviceListReply.httpCode});

    let deviceList = deviceListReply.deviceList as Device[];

    // we sort by the specified key (deviceId or lastOnline)
    this.sort(deviceList, query.orderBy, query.ascDesc);

    return Promise.resolve({devicePageList:this.paginateDeviceList(deviceList, query.pageSize)});
  }

  public async loadDeviceConfiguration(query: DeviceConfigurationQuery): Promise<DeviceConfigurationSet | Error> {
    const reply = await curCoreClient.loadDeviceConfiguration(query);
    if(reply.errorMsg) {
      return Promise.reject(new Error(reply.errorMsg));
    }
    return Promise.resolve(reply.configuration);
  }

  public async requestConfiguration(query: DeviceConfigurationRequest): Promise<DownlinkItem[] | Error> {
    const reply = await curCoreClient.requestDeviceConfiguration(query);
    if (reply.errorMsg) {
      return Promise.reject(new Error(reply.errorMsg));
    }
    return Promise.resolve(reply.queue);
  }

  public async sendDeviceCommand(query: DeviceCommandReq): Promise<DownlinkItem[] | Error> {
    const reply = await curCoreClient.sendDeviceCommand(query);
    if (reply.errorMsg) {
      return Promise.reject(new Error(reply.errorMsg));
    }
    return Promise.resolve(reply.queue);
  }

  public async getDownlinksQueue(query: GetDeviceReq): Promise<DownlinkItem[] | Error> {
    const reply = await curCoreClient.getDownlinksQueue(query);
    if (reply.errorMsg) {
      return Promise.reject(new Error(reply.errorMsg));
    }
    return Promise.resolve(reply.queue);
  }

  public async getDeviceChannelReadings(query: DeviceChannelReadingsQuery): Promise<Reading[]> {
    
    // we make sure there is a limit set of 1000
    const curQuery = Object.assign({}, query, {limit: 1000});

    let combReadingList: CombinedReading[] = [];

    while(true) {
      
      const reply = await curCoreClient.getMessages(curQuery);

      if(reply.errorMsg)
        return Promise.reject(reply.errorMsg);

      const replyMessageList:Message[] = reply.messageList;

      if (replyMessageList.length === 0)
        break;

      // we filter the messages that have some readings and map to the CombinedReading
      const replyReadingList:CombinedReading[] = replyMessageList
        .filter(msg => msg.readingList !== undefined)
        .map(msg => ({ts:msg.ts, values:msg.readingList as Channel[]}))

      // we update our fromTs to the last element of the previous query
      const newFromTs = replyMessageList[replyMessageList.length - 1].ts;

      if (newFromTs === curQuery.fromTs)
        break;

      curQuery.fromTs = newFromTs + 1;

      // we add to readings to the return list
      combReadingList = combReadingList.concat(replyReadingList);

      // we continue;
    }

    // we filter out the readings that don't match our channel
    const retList:Reading[] = [];

    for(const combReading of combReadingList) {

      const chReading = combReading.values.find(read => parseInt(`${read.channelId}`, 10) === query.channelId);

      if(!chReading)
        continue;

      retList.push({
        ts:combReading.ts,
        type:chReading.type,
        unit:chReading.unit,
        value:chReading.value,
        label:chReading.label,
        outOfOrderUplinkCnt:chReading.outOfOrderUplinkCnt ? chReading.outOfOrderUplinkCnt : false,
        outOfOrderTs:chReading.outOfOrderTs ? chReading.outOfOrderTs : false,
      });
    }

    return Promise.resolve(retList);
  }

  public sort(list: any[], key: string, ascDesc: 'asc'|'desc') {

    // we sort asc first
    list.sort((a, b) => {

      if (a[key] < b[key] || a[key] === undefined) {
        return ascDesc === 'asc' ? -1 : 1;
      } else if (a[key] > b[key] || b[key] === undefined) {
        return ascDesc === 'asc' ? +1 : -1;
      } else
        return 0;
    });

  }

  // private async getAllDevices(): Promise<{ errorMsg?: string, httpCode?: number, deviceList?: Device[] }> {
  //   // from MDN docs: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Array/from#sequence_generator_range
  //   const range = (start: number, stop: number, step: number) => Array.from({ length: (stop - start) / step + 1 }, (_, i) => start + (i * step));
  //   const deviceLimit = 100000;
  //   let retList: Device[] = [];

  //   // hacky of doing things for now
  //   await Promise.all(
  //     range(0, deviceLimit, 100000).map(async (skip) => {
  //       const resp = await curCoreClient.getDevices({ skip, limit: 100000 });
  //       if (resp.deviceList && resp.deviceList.length > 0)
  //         retList.push(...resp.deviceList);
  //     })
  //   );

  //   return Promise.resolve({ deviceList: retList });
  // }

  private async getAllDevices(): Promise<{ errorMsg?: string, httpCode?: number, deviceList?: Device[] }> {
  const step = 100000;
  let retList: Device[] = [];
  let currentSkip = 1000; 
  let moreData = true;
  while (moreData) {
    const resp = await curCoreClient.getDevices({
      skip: currentSkip,
      limit: step
    });
    console.log("skip: ", currentSkip, " step: ", step, " resp: ", resp);
    if (resp.errorMsg || !resp.deviceList) {
      return Promise.resolve({ errorMsg: resp.errorMsg, httpCode: resp.httpCode, deviceList: retList });
    }

    const fetched = resp.deviceList.length;
    retList.push(...resp.deviceList);
    currentSkip += fetched;

    console.log("skip: ", currentSkip, " step: ", step, " fetched: ", fetched);

    if (fetched < step) {
      moreData = false; // no more data
    }
  }

  return Promise.resolve({ deviceList: retList });
}


  private paginateDeviceList(deviceList: Device[], pageSize: number): DevicePage[] {

    const chunkedList = _.chunk(deviceList, pageSize);

    return chunkedList.map((chunkedListItem:any, index:number) => ({
      pageNum: index,
      deviceList: chunkedListItem,
    }));
  }

}

export interface GetDevicesQuery {
  orderBy: 'deviceId'|'lastOnline';
  ascDesc: 'asc'|'desc';
  pageSize: number;
  filter: string;
  skip?: number;
  limit?: number;
}

export interface DeviceChannelReadingsQuery {
  deviceId: string;
  channelId: number;
  fromTs: number;
  toTs: number;
}

export interface DeviceConfigurationQuery {
  deviceId: string;
}

export interface DeviceConfigurationRequest {
  deviceId: string;
  params?: number[];
}

export interface GetDevicesReply {
  devicePageList?:DevicePage[];
}

export interface DevicePage {
  pageNum: number;
  deviceList: Device[];
}

export const curDeviceService = new DeviceService();
